/**
 * Hala AI Learning System - Session Analysis Full Page
 * Comprehensive view of session data, messages, feedbacks, and decision traces
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, MessageSquare, User, Clock, CheckCircle, AlertTriangle,
  ChevronDown, Play, Pause, RefreshCw, Eye, Brain, Target,
  Zap, TrendingUp, AlertCircle, Check, XCircle, Edit2, Trash2,
  Send, ThumbsUp, ThumbsDown, HelpCircle, BookOpen, Sparkles,
  FileText, BarChart3, Activity, Calendar, Phone, Mail, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  goldLight: '#D4B896',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const STATE_CONFIG = {
  WELCOME: { label: 'ترحيب', color: COLORS.primary, icon: Clock },
  DISCOVERY: { label: 'استكشاف', color: COLORS.warning, icon: Play },
  REFLECTION: { label: 'انعكاس', color: COLORS.gold, icon: Pause },
  RESULT_READY: { label: 'نتائج جاهزة', color: COLORS.success, icon: CheckCircle },
  PLAN_READY: { label: 'خطة جاهزة', color: COLORS.success, icon: CheckCircle },
  SERVICE_ACTIVE: { label: 'خدمة نشطة', color: COLORS.success, icon: Play },
  SERVICE_IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.warning, icon: Clock },
  SERVICE_SUBMITTED: { label: 'خدمة مكتملة', color: COLORS.success, icon: CheckCircle },
  CLIENT_INFO_CAPTURE: { label: 'جمع المعلومات', color: COLORS.warning, icon: User },
};

const FEEDBACK_TYPES = {
  correct: { label: 'صحيحة', color: COLORS.success, icon: Check },
  partial: { label: 'مقبولة', color: COLORS.warning, icon: AlertCircle },
  wrong: { label: 'خاطئة', color: COLORS.error, icon: XCircle },
};

const MESSAGE_TYPES = {
  question: { label: 'سؤال', icon: HelpCircle, color: COLORS.info },
  recommendation: { label: 'توصية', icon: Target, color: COLORS.success },
  info: { label: 'معلومة', icon: BookOpen, color: COLORS.gold },
  decision: { label: 'قرار', icon: Zap, color: COLORS.error },
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function StatCard({ icon: Icon, label, value, color, subtext }) {
  return (
    <div style={{
      backgroundColor: COLORS.white,
      padding: '20px',
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        backgroundColor: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={24} color={color} />
      </div>
      <div>
        <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>{label}</p>
        <p style={{ fontSize: '24px', fontWeight: 700, color: color, margin: 0 }}>{value}</p>
        {subtext && <p style={{ fontSize: '11px', color: COLORS.textLight, margin: '2px 0 0' }}>{subtext}</p>}
      </div>
    </div>
  );
}

function ChatBubble({ message, feedback, isLast }) {
  const isHala = message.sender === 'assistant';
  
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString('ar-SA', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch { return ''; }
  };
  
  const detectMessageType = (text) => {
    if (!text) return 'info';
    if (text.includes('؟') || text.includes('?')) return 'question';
    if (text.includes('نوصي') || text.includes('أنصح') || text.includes('خطة')) return 'recommendation';
    if (text.includes('قرار') || text.includes('تم') || text.includes('اختر')) return 'decision';
    return 'info';
  };
  
  const msgType = detectMessageType(message.message_text);
  const TypeIcon = MESSAGE_TYPES[msgType]?.icon || BookOpen;
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: isHala ? 'row' : 'row-reverse',
      gap: '12px',
      marginBottom: '20px',
    }}>
      <div style={{
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        backgroundColor: isHala ? COLORS.primary : COLORS.bgDark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        {isHala ? (
          <Sparkles size={20} color={COLORS.gold} />
        ) : (
          <User size={20} color={COLORS.textMuted} />
        )}
      </div>
      
      <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {isHala && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TypeIcon size={14} color={MESSAGE_TYPES[msgType]?.color} />
            <span style={{ fontSize: '11px', color: MESSAGE_TYPES[msgType]?.color, fontWeight: 500 }}>
              {MESSAGE_TYPES[msgType]?.label}
            </span>
            {feedback && (
              <span style={{
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 600,
                backgroundColor: FEEDBACK_TYPES[feedback.feedback_type]?.color + '20',
                color: FEEDBACK_TYPES[feedback.feedback_type]?.color,
              }}>
                {FEEDBACK_TYPES[feedback.feedback_type]?.label}
              </span>
            )}
          </div>
        )}
        
        <div style={{
          padding: '16px 20px',
          borderRadius: isHala ? '4px 20px 20px 20px' : '20px 4px 20px 20px',
          backgroundColor: isHala ? COLORS.primary : COLORS.white,
          color: isHala ? COLORS.white : COLORS.text,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: isHala ? 'none' : `1px solid ${COLORS.border}`,
          position: 'relative',
        }}>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
            {message.message_text || message.content}
          </p>
        </div>
        
        <span style={{ fontSize: '11px', color: COLORS.textLight, alignSelf: isHala ? 'flex-start' : 'flex-end' }}>
          {formatTime(message.created_at || message.timestamp)}
        </span>
      </div>
    </div>
  );
}

function DecisionTraceCard({ trace }) {
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${COLORS.border}`,
      borderRight: `4px solid ${COLORS.primary}`,
    }}>
      <p style={{ 
        margin: '0 0 16px', 
        fontSize: '13px', 
        color: COLORS.text,
        backgroundColor: COLORS.bg,
        padding: '12px',
        borderRadius: '8px',
        lineHeight: 1.6,
      }}>
        {trace.message_text}
      </p>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div style={{ backgroundColor: COLORS.bg, padding: '12px', borderRadius: '8px' }}>
          <p style={{ fontSize: '10px', color: COLORS.textMuted, margin: '0 0 4px' }}>Intent المكتشف</p>
          <p style={{ fontSize: '13px', fontWeight: 600, color: COLORS.primary, margin: 0 }}>
            {trace.detected_intent || 'غير محدد'}
          </p>
        </div>
        <div style={{ backgroundColor: COLORS.bg, padding: '12px', borderRadius: '8px' }}>
          <p style={{ fontSize: '10px', color: COLORS.textMuted, margin: '0 0 4px' }}>Need المطبق</p>
          <p style={{ fontSize: '13px', fontWeight: 600, color: COLORS.primary, margin: 0 }}>
            {trace.detected_need || 'غير محدد'}
          </p>
        </div>
      </div>
      
      {trace.applied_rules?.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 8px' }}>القواعد المطبقة:</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {trace.applied_rules.map((rule, idx) => (
              <span key={idx} style={{
                padding: '4px 10px',
                borderRadius: '6px',
                backgroundColor: COLORS.infoLight,
                color: COLORS.info,
                fontSize: '11px',
                fontWeight: 500,
              }}>
                {rule.rule_id} (P:{rule.priority})
              </span>
            ))}
          </div>
        </div>
      )}
      
      {trace.suggested_services?.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 8px' }}>الخدمات المقترحة:</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {trace.suggested_services.map((svc, idx) => (
              <span key={idx} style={{
                padding: '4px 10px',
                borderRadius: '6px',
                backgroundColor: COLORS.successLight,
                color: COLORS.success,
                fontSize: '11px',
                fontWeight: 500,
              }}>
                {svc}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {trace.confidence_factors?.length > 0 && (
        <div>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 8px' }}>عوامل الثقة:</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {trace.confidence_factors.map((factor, idx) => (
              <span key={idx} style={{
                padding: '4px 10px',
                borderRadius: '6px',
                backgroundColor: COLORS.warningLight,
                color: COLORS.warning,
                fontSize: '10px',
                fontWeight: 500,
              }}>
                {factor.label} (+{Math.round(factor.boost * 100)}%)
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function SessionAnalysisPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversation');
  const chatEndRef = useRef(null);
  
  const fetchAnalysis = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/learning/session/${sessionId}/full-analysis`,
        { headers: getAuthHeaders() }
      );
      setAnalysis(res.data);
    } catch (e) {
      console.error('Failed to fetch analysis:', e);
      if (e.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);
  
  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch { return dateStr; }
  };
  
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textMuted }}>
        جاري تحميل تحليل الجلسة...
      </div>
    );
  }
  
  if (!analysis) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: COLORS.error }}>
        لم يتم العثور على الجلسة
      </div>
    );
  }
  
  const { session_data, messages, feedbacks, decision_trace, summary, client_info, diagnosis } = analysis;
  const status = STATE_CONFIG[session_data?.current_state] || STATE_CONFIG.WELCOME;
  const StatusIcon = status.icon;
  
  return (
    <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg }}>
      {/* Header */}
      <div style={{
        backgroundColor: COLORS.primary,
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button
            onClick={() => navigate('/admin/sessions')}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: 'rgba(255,255,255,0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowRight size={20} color={COLORS.white} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: COLORS.white }}>
              تحليل الجلسة
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
              {sessionId?.substring(0, 24)}...
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: '8px',
            backgroundColor: `${status.color}20`,
            color: COLORS.white,
            fontSize: '13px',
            fontWeight: 500,
          }}>
            <StatusIcon size={16} />
            {status.label}
          </span>
          <button
            onClick={fetchAnalysis}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: 'rgba(255,255,255,0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RefreshCw size={18} color={COLORS.white} />
          </button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div style={{ padding: '24px 32px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard 
            icon={MessageSquare} 
            label="إجمالي الرسائل" 
            value={summary?.total_messages || 0} 
            color={COLORS.primary}
            subtext={`هلا: ${summary?.ai_messages || 0} | المستخدم: ${summary?.user_messages || 0}`}
          />
          <StatCard 
            icon={BarChart3} 
            label="دقة الردود" 
            value={`${summary?.accuracy_rate || 0}%`} 
            color={summary?.accuracy_rate >= 80 ? COLORS.success : summary?.accuracy_rate >= 50 ? COLORS.warning : COLORS.error}
            subtext={`${summary?.evaluated_count || 0} تم تقييمها`}
          />
          <StatCard 
            icon={Check} 
            label="ردود صحيحة" 
            value={summary?.correct_count || 0} 
            color={COLORS.success}
          />
          <StatCard 
            icon={AlertCircle} 
            label="ردود مقبولة" 
            value={summary?.partial_count || 0} 
            color={COLORS.warning}
          />
          <StatCard 
            icon={XCircle} 
            label="ردود خاطئة" 
            value={summary?.wrong_count || 0} 
            color={COLORS.error}
          />
        </div>
        
        {/* Client & Diagnosis Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Client Info */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={20} color={COLORS.primary} />
              معلومات العميل
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>الاسم</p>
                <p style={{ fontSize: '15px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {client_info?.name || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>رقم الجوال</p>
                <p style={{ fontSize: '15px', fontWeight: 500, color: COLORS.text, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Phone size={14} color={COLORS.textMuted} />
                  {client_info?.mobile || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>البريد الإلكتروني</p>
                <p style={{ fontSize: '15px', fontWeight: 500, color: COLORS.text, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Mail size={14} color={COLORS.textMuted} />
                  {client_info?.email || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>المدينة</p>
                <p style={{ fontSize: '15px', fontWeight: 500, color: COLORS.text, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MapPin size={14} color={COLORS.textMuted} />
                  {client_info?.city || 'غير محدد'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Diagnosis Info */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Brain size={20} color={COLORS.gold} />
              نتائج التشخيص
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>مرحلة المشروع</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {diagnosis?.project_stage || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>التحدي الرئيسي</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {diagnosis?.main_problem || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>نوع الدعم</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {diagnosis?.support_type || 'غير محدد'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 4px' }}>مجال العمل</p>
                <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                  {diagnosis?.sector || 'غير محدد'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '4px',
          backgroundColor: COLORS.white,
          padding: '6px',
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          marginBottom: '24px',
        }}>
          {[
            { id: 'conversation', label: 'المحادثة', icon: MessageSquare },
            { id: 'trace', label: 'تتبع القرارات', icon: Target },
            { id: 'timeline', label: 'الجدول الزمني', icon: Activity },
          ].map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: activeTab === tab.id ? COLORS.primary : 'transparent',
                  color: activeTab === tab.id ? COLORS.white : COLORS.textMuted,
                  fontSize: '14px',
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                }}
              >
                <TabIcon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          border: `1px solid ${COLORS.border}`,
          minHeight: '500px',
        }}>
          {activeTab === 'conversation' && (
            <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
              {messages?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textMuted }}>
                  <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '16px' }}>لا توجد رسائل في هذه الجلسة</p>
                </div>
              ) : (
                <>
                  {messages?.map((msg, idx) => (
                    <ChatBubble
                      key={msg.message_id || idx}
                      message={msg}
                      feedback={feedbacks?.[msg.message_id]}
                      isLast={idx === messages.length - 1}
                    />
                  ))}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>
          )}
          
          {activeTab === 'trace' && (
            <div style={{ padding: '32px' }}>
              {decision_trace?.filter(t => t.is_recommendation)?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '800px', margin: '0 auto' }}>
                  {decision_trace.filter(t => t.is_recommendation).map((trace, idx) => (
                    <DecisionTraceCard key={idx} trace={trace} />
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textMuted }}>
                  <Target size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '16px' }}>لا توجد توصيات في هذه الجلسة بعد</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'timeline' && (
            <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
              {analysis?.timeline?.length > 0 ? (
                <div style={{ position: 'relative', paddingRight: '30px' }}>
                  {/* Timeline Line */}
                  <div style={{
                    position: 'absolute',
                    right: '10px',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: COLORS.border,
                  }} />
                  
                  {analysis.timeline.map((item, idx) => (
                    <div key={idx} style={{ 
                      position: 'relative', 
                      marginBottom: '24px',
                      paddingRight: '40px',
                    }}>
                      {/* Timeline Dot */}
                      <div style={{
                        position: 'absolute',
                        right: '4px',
                        top: '4px',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        backgroundColor: item.type === 'message' 
                          ? (item.sender === 'assistant' ? COLORS.primary : COLORS.bgDark)
                          : COLORS.gold,
                        border: `3px solid ${COLORS.white}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }} />
                      
                      <div style={{
                        backgroundColor: COLORS.bg,
                        padding: '16px',
                        borderRadius: '12px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: item.type === 'message' 
                              ? (item.sender === 'assistant' ? COLORS.primary : COLORS.textMuted)
                              : COLORS.gold,
                          }}>
                            {item.type === 'message' 
                              ? (item.sender === 'assistant' ? 'هلا' : 'المستخدم')
                              : item.event_type}
                          </span>
                          <span style={{ fontSize: '11px', color: COLORS.textLight }}>
                            {formatDate(item.timestamp)}
                          </span>
                        </div>
                        {item.type === 'message' ? (
                          <p style={{ margin: 0, fontSize: '13px', color: COLORS.text, lineHeight: 1.6 }}>
                            {item.text?.substring(0, 150)}{item.text?.length > 150 ? '...' : ''}
                          </p>
                        ) : (
                          <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>
                            {JSON.stringify(item.payload)}
                          </p>
                        )}
                        {item.feedback && (
                          <span style={{
                            display: 'inline-block',
                            marginTop: '8px',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            backgroundColor: FEEDBACK_TYPES[item.feedback.feedback_type]?.color + '20',
                            color: FEEDBACK_TYPES[item.feedback.feedback_type]?.color,
                          }}>
                            تقييم: {FEEDBACK_TYPES[item.feedback.feedback_type]?.label}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textMuted }}>
                  <Activity size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '16px' }}>لا توجد أحداث في الجدول الزمني</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
